/**
 * QuickCaptureModal — universal lightweight entry form for Inquiry/Lead/Idea/Note.
 *
 * Triggered from a global FAB (AdminLayout) or any "+" button via:
 *   window.dispatchEvent(new CustomEvent("quick-capture", { detail: { type: "inquiry" } }))
 *
 * Minimum required: type + title. Everything else is optional.
 * Auto-detects email/phone via clipboard-paste heuristics.
 *
 * iter325: Customer autocomplete — search existing customers and link the
 * capture to them instead of always creating a new card.
 */
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Zap, Lightbulb, Target, FileText, User as UserIcon, Phone, Mail, Building2,
  X, Loader2, ChevronRight, Search, Link2, UserPlus,
} from "lucide-react";
import { API_V1 } from "../config/api";

const TYPE_OPTIONS = [
  { key: "inquiry", label: "Poptávka", icon: Zap,        color: "blue",    desc: "Konkrétní zájem zákazníka → POP-…" },
  { key: "lead",    label: "Lead",     icon: Target,     color: "violet",  desc: "Potenciální kontakt (do pipeline)" },
  { key: "idea",    label: "Nápad",    icon: Lightbulb,  color: "amber",   desc: "Myšlenka / poznámka pro tým" },
  { key: "note",    label: "Poznámka", icon: FileText,   color: "slate",   desc: "Rychlá poznámka (kanban)" },
];

const COLOR_RING = {
  blue:   "bg-blue-50    border-blue-300    text-blue-700",
  violet: "bg-violet-50  border-violet-300  text-violet-700",
  amber:  "bg-amber-50   border-amber-300   text-amber-700",
  slate:  "bg-slate-50   border-slate-300   text-slate-700",
};

const EMAIL_RX = /[\w.+-]+@[\w-]+\.[\w.-]+/i;
const PHONE_RX = /(?:\+?\d[\d\s\-/()]{7,})/;

export default function QuickCaptureModal({ defaultType = "inquiry", onClose, onSaved }) {
  const [type, setType] = useState(defaultType);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [contact, setContact] = useState({ name: "", email: "", phone: "", company: "" });
  const [source, setSource] = useState("manual");
  const [priority, setPriority] = useState("normal");
  const [saving, setSaving] = useState(false);
  // iter325 — customer linking state
  const [linkedCustomer, setLinkedCustomer] = useState(null);   // {customer_id, name, …}
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [custOpen, setCustOpen] = useState(false);
  const [custLoading, setCustLoading] = useState(false);
  const titleRef = useRef(null);
  const custDebounce = useRef(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  // Heuristic: when user pastes long text into description, try to extract email/phone
  const onDescriptionPaste = (e) => {
    const text = e.clipboardData?.getData("text") || "";
    const emailMatch = text.match(EMAIL_RX);
    const phoneMatch = text.match(PHONE_RX);
    if (emailMatch && !contact.email) setContact((c) => ({ ...c, email: emailMatch[0] }));
    if (phoneMatch && !contact.phone) setContact((c) => ({ ...c, phone: phoneMatch[0].trim() }));
  };

  // ── Customer search (debounced) ────────────────────────────────
  useEffect(() => {
    if (linkedCustomer) return;
    if (!custOpen) return;
    if (custDebounce.current) clearTimeout(custDebounce.current);
    custDebounce.current = setTimeout(async () => {
      setCustLoading(true);
      try {
        const r = await axios.get(`${API_V1}/customers/search`, {
          params: { q: custQuery.trim() },
        });
        setCustResults(Array.isArray(r.data) ? r.data.slice(0, 12) : []);
      } catch {
        setCustResults([]);
      } finally {
        setCustLoading(false);
      }
    }, 220);
    return () => custDebounce.current && clearTimeout(custDebounce.current);
  }, [custQuery, custOpen, linkedCustomer]);

  const pickCustomer = (c) => {
    setLinkedCustomer(c);
    setContact({
      name: c.name || "",
      email: c.email || "",
      phone: c.phone || "",
      company: c.company || "",
    });
    setCustOpen(false);
    setCustQuery("");
  };

  const clearLinkedCustomer = () => {
    setLinkedCustomer(null);
    setContact({ name: "", email: "", phone: "", company: "" });
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Vyplňte název");
    setSaving(true);
    try {
      const r = await axios.post(`${API_V1}/quick-capture`, {
        type, title: title.trim(), description: description.trim(),
        contact, source, priority,
        customer_id: linkedCustomer?.customer_id || null,
        auto_create_customer: !linkedCustomer,
      });
      toast.success(`Vytvořeno ${r.data.id}`, {
        action: r.data.url ? { label: "Otevřít", onClick: () => (window.location.href = r.data.url) } : undefined,
      });
      onSaved?.(r.data);
      onClose?.();
    } catch (err) {
      toast.error("Uložení selhalo: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") onClose?.();
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      onClick={onClose}
      onKeyDown={onKeyDown}
      data-testid="quick-capture-modal">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl border border-slate-200 mt-12 sm:mt-20"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-violet-100 rounded-lg">
              <Zap className="w-4 h-4 text-violet-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-800">Rychlý záznam</h2>
              <p className="text-xs text-slate-500">Esc zavře, ⌘/Ctrl + Enter uloží</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="quick-capture-close">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        {/* Type selector */}
        <div className="px-5 pt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {TYPE_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const active = type === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setType(opt.key)}
                  data-testid={`quick-capture-type-${opt.key}`}
                  className={`p-3 border rounded-lg text-left transition-all ${
                    active ? `${COLOR_RING[opt.color]} ring-2 ring-${opt.color}-300 shadow-sm` : "bg-white border-slate-200 hover:bg-slate-50 text-slate-600"
                  }`}>
                  <Icon className="w-4 h-4 mb-1" />
                  <div className="text-xs font-semibold">{opt.label}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{opt.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Form */}
        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Název / shrnutí *</label>
            <input
              ref={titleRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="např. Pan Novák — FVE 6 kW, RD Říčany"
              data-testid="quick-capture-title"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:border-violet-500 focus:ring-1 focus:ring-violet-300 outline-none" />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Popis / co se stalo</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onPaste={onDescriptionPaste}
              placeholder="Volal z čísla 605... chce FVE + baterii, ozvat se v pátek odpoledne. Vložením textu se pokusím vytáhnout e-mail a telefon."
              rows={3}
              data-testid="quick-capture-description"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:border-violet-500 focus:ring-1 focus:ring-violet-300 outline-none resize-none" />
          </div>

          {/* Contact info – only for inquiry/lead */}
          {(type === "inquiry" || type === "lead") && (
            <div className="bg-slate-50 p-3 rounded-lg space-y-2">
              {/* Customer linker */}
              <div>
                <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1 block">
                  Zákazník
                </label>
                {linkedCustomer ? (
                  <div
                    data-testid="quick-capture-customer-linked"
                    className="flex items-center justify-between gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md">
                    <div className="flex items-center gap-2 min-w-0">
                      <Link2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-emerald-900 truncate">
                          {linkedCustomer.name || linkedCustomer.company || linkedCustomer.email}
                        </div>
                        <div className="text-[10px] text-emerald-700 truncate">
                          {linkedCustomer.customer_number}
                          {linkedCustomer.email ? ` · ${linkedCustomer.email}` : ""}
                          {linkedCustomer.phone ? ` · ${linkedCustomer.phone}` : ""}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={clearLinkedCustomer}
                      data-testid="quick-capture-customer-clear"
                      title="Zrušit propojení"
                      className="p-1 text-emerald-700 hover:bg-emerald-100 rounded">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    <input
                      value={custQuery}
                      onChange={(e) => { setCustQuery(e.target.value); setCustOpen(true); }}
                      onFocus={() => setCustOpen(true)}
                      onBlur={() => setTimeout(() => setCustOpen(false), 180)}
                      placeholder="Hledat existujícího zákazníka (jméno, e-mail, telefon, IČO…)"
                      data-testid="quick-capture-customer-search"
                      className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-md text-xs bg-white outline-none focus:border-violet-400" />
                    {custOpen && (
                      <div
                        data-testid="quick-capture-customer-results"
                        className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                        {custLoading && (
                          <div className="px-3 py-2 text-[11px] text-slate-500 flex items-center gap-1.5">
                            <Loader2 className="w-3 h-3 animate-spin" /> Hledám…
                          </div>
                        )}
                        {!custLoading && custResults.length === 0 && (
                          <div className="px-3 py-2 text-[11px] text-slate-500 flex items-center gap-1.5">
                            <UserPlus className="w-3 h-3" />
                            Žádný zákazník — vyplňte kontakt níže, vytvoří se nový.
                          </div>
                        )}
                        {custResults.map((c) => (
                          <button
                            key={c.customer_id}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => pickCustomer(c)}
                            data-testid={`quick-capture-customer-result-${c.customer_id}`}
                            className="w-full text-left px-3 py-1.5 hover:bg-violet-50 flex flex-col gap-0.5 border-b border-slate-50 last:border-b-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-semibold text-slate-800 truncate">
                                {c.name || c.company || c.email || "Bez jména"}
                              </span>
                              <span className="text-[10px] text-slate-500 font-mono shrink-0">{c.customer_number}</span>
                            </div>
                            <div className="text-[10px] text-slate-500 truncate">
                              {[c.company, c.email, c.phone].filter(Boolean).join(" · ")}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Contact fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="relative">
                  <UserIcon className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
                  <input
                    value={contact.name}
                    onChange={(e) => setContact({ ...contact, name: e.target.value })}
                    placeholder="Jméno / firma"
                    data-testid="quick-capture-name"
                    disabled={!!linkedCustomer}
                    className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-md text-xs bg-white outline-none focus:border-violet-400 disabled:bg-slate-100 disabled:text-slate-500" />
                </div>
                <div className="relative">
                  <Building2 className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
                  <input
                    value={contact.company}
                    onChange={(e) => setContact({ ...contact, company: e.target.value })}
                    placeholder="Společnost"
                    data-testid="quick-capture-company"
                    disabled={!!linkedCustomer}
                    className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-md text-xs bg-white outline-none focus:border-violet-400 disabled:bg-slate-100 disabled:text-slate-500" />
                </div>
                <div className="relative">
                  <Mail className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
                  <input
                    type="email"
                    value={contact.email}
                    onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    placeholder="E-mail"
                    data-testid="quick-capture-email"
                    disabled={!!linkedCustomer}
                    className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-md text-xs bg-white outline-none focus:border-violet-400 disabled:bg-slate-100 disabled:text-slate-500" />
                </div>
                <div className="relative">
                  <Phone className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
                  <input
                    value={contact.phone}
                    onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                    placeholder="Telefon"
                    data-testid="quick-capture-phone"
                    disabled={!!linkedCustomer}
                    className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-md text-xs bg-white outline-none focus:border-violet-400 disabled:bg-slate-100 disabled:text-slate-500" />
                </div>
              </div>
            </div>
          )}

          {/* Source + priority */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1 block">Zdroj</label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                data-testid="quick-capture-source"
                className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-xs bg-white">
                <option value="manual">Manuálně</option>
                <option value="phone">Telefon</option>
                <option value="email">E-mail</option>
                <option value="web">Web</option>
                <option value="chat">Chat</option>
                <option value="referral">Doporučení</option>
                <option value="other">Jiné</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1 block">Priorita</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                data-testid="quick-capture-priority"
                className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-xs bg-white">
                <option value="low">Nízká</option>
                <option value="normal">Normální</option>
                <option value="high">Vysoká</option>
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
          <div className="text-[10px] text-slate-500">
            Uloží se jako <code className="bg-white px-1 py-0.5 rounded font-semibold">{type === "inquiry" ? "Poptávka (POP-…)" : type === "lead" ? "Lead (POP-…)" : "Poznámka (NOTE-…)"}</code>
            {linkedCustomer && (
              <span className="ml-2 text-emerald-700">· propojeno se zákazníkem <strong>{linkedCustomer.customer_number}</strong></span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-200 rounded-lg"
              data-testid="quick-capture-cancel">
              Zrušit
            </button>
            <button
              onClick={submit}
              disabled={saving || !title.trim()}
              data-testid="quick-capture-save"
              className="px-4 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs rounded-lg flex items-center gap-1.5 disabled:opacity-50">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronRight className="w-3 h-3" />}
              Uložit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
